import type { GridSelection } from "@glideapps/glide-data-grid";
import { useEffect, useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type {
  IAiSpreadsheetLoadedSheet,
  IAiSpreadsheetRegion,
  IAiSpreadsheetRegionInput,
} from "@/payroll/ai-script/types";
import { getSheetContentBounds } from "./manual-row-utils";
import {
  proposeSpreadsheetRegion,
  updateSpreadsheetRegionLimits,
  validateSpreadsheetRegionDraft,
  type SpreadsheetRegionDraft,
} from "./spreadsheet-region";
import { SpreadsheetRegionColumnControls } from "./spreadsheet-region-column-controls";
import { formatColumnLetter } from "./utils";

/**
 * "U" → 21. Accepts what a person would actually type: lower case, surrounding
 * space, and the `$` of an absolute reference pasted out of a formula. Returns
 * null for anything that is not a column reference, so a half-typed field can be
 * left alone rather than snapping back.
 */
export const columnNumberFromName = (value: string): number | null => {
  const letters = value.replace(/\$/g, "").trim().toUpperCase();
  if (!/^[A-Z]{1,3}$/.test(letters)) return null;

  let column = 0;
  for (const letter of letters) {
    column = column * 26 + (letter.charCodeAt(0) - 64);
  }
  // Excel's own ceiling; the API rejects anything past its own limit too.
  return column >= 1 && column <= 16384 ? column : null;
};

interface SpreadsheetRegionControlProps {
  sheet: IAiSpreadsheetLoadedSheet;
  selection: GridSelection;
  existingRegion?: IAiSpreadsheetRegion;
  onOpenChange: (open: boolean) => void;
  isSaving: boolean;
  onPreviewChange: (draft: SpreadsheetRegionDraft | null) => void;
  onCreate: (region: IAiSpreadsheetRegionInput) => Promise<void>;
  onUpdate: (
    regionId: string,
    region: IAiSpreadsheetRegionInput,
  ) => Promise<void>;
  onDelete: (regionId: string) => Promise<void>;
}

export const SpreadsheetRegionControl = ({
  sheet,
  selection,
  existingRegion,
  onOpenChange,
  isSaving,
  onPreviewChange,
  onCreate,
  onUpdate,
  onDelete,
}: SpreadsheetRegionControlProps) => {
  const regions = sheet.regions ?? [];
  const [initialDraft] = useState<SpreadsheetRegionDraft | null>(() =>
    existingRegion
      ? {
          header_row: existingRegion.header_row,
          record_start_row: existingRegion.record_start_row,
          record_end_row: existingRegion.record_end_row,
          start_col: existingRegion.start_col,
          end_col: existingRegion.end_col,
          column_settings: existingRegion.column_settings,
        }
      : proposeSpreadsheetRegion(sheet, selection),
  );
  const [draft, setDraft] = useState(initialDraft);
  const { maxContentRow, maxContentCol } = getSheetContentBounds(sheet.cells);

  // The parent mounts one control per open session. Start its grid preview once,
  // and clear the preview when that session closes.
  useEffect(() => {
    onPreviewChange(initialDraft);
    return () => onPreviewChange(null);
  }, [initialDraft, onPreviewChange]);

  const errors = draft
    ? validateSpreadsheetRegionDraft(draft, {
        regions,
        editedRegionId: existingRegion?.id,
        mergedRanges: sheet.merged_ranges ?? [],
        bounds: { maxRow: maxContentRow, maxCol: maxContentCol },
      })
    : ["Select one cell or a rectangular range first."];

  const changeLimit = (
    key: "header_row" | "record_end_row" | "start_col" | "end_col",
    value: number,
  ) => {
    if (!draft || !Number.isInteger(value)) return;
    const next = updateSpreadsheetRegionLimits(draft, {
      ...draft,
      [key]: value,
    });
    setDraft(next);
    onPreviewChange(next);
  };

  /**
   * What the user has typed into each column field, while they are typing. Held
   * separately from the draft so a field can pass through states that do not parse
   * — clearing "U" to type "AB" goes through "" — without the value jumping back
   * under the cursor. Null means "show the draft's own letter".
   */
  const [limitFieldText, setLimitFieldText] = useState<{
    header_row: string | null;
    record_end_row: string | null;
    start_col: string | null;
    end_col: string | null;
  }>({
    header_row: null,
    record_end_row: null,
    start_col: null,
    end_col: null,
  });

  const isColumnFieldValid = {
    start_col:
      limitFieldText.start_col === null ||
      columnNumberFromName(limitFieldText.start_col) !== null,
    end_col:
      limitFieldText.end_col === null ||
      columnNumberFromName(limitFieldText.end_col) !== null,
  };
  const areRowFieldsValid =
    (limitFieldText.header_row === null ||
      /^\d+$/.test(limitFieldText.header_row)) &&
    (limitFieldText.record_end_row === null ||
      /^\d+$/.test(limitFieldText.record_end_row));
  const areColumnFieldsValid =
    isColumnFieldValid.start_col && isColumnFieldValid.end_col;
  const validationErrors = [
    ...errors,
    ...(areRowFieldsValid ? [] : ["Enter valid row numbers."]),
    ...(areColumnFieldsValid
      ? []
      : ["Enter valid column letters from A through XFD."]),
  ];

  const changeRowLimit = (
    key: "header_row" | "record_end_row",
    value: string,
  ) => {
    setLimitFieldText((current) => ({ ...current, [key]: value }));
    if (!/^\d+$/.test(value)) return;
    changeLimit(key, Number(value));
  };

  const changeColumnLimit = (
    key: "start_col" | "end_col",
    value: string,
  ): void => {
    setLimitFieldText((current) => ({ ...current, [key]: value }));
    const column = columnNumberFromName(value);
    if (column !== null) changeLimit(key, column);
  };

  const close = () => {
    onOpenChange(false);
  };

  const save = async () => {
    if (!draft || validationErrors.length > 0) return;
    try {
      if (existingRegion) await onUpdate(existingRegion.id, draft);
      else await onCreate(draft);
      close();
    } catch {
      // The parent shows the API validation message. Keep the draft open.
    }
  };

  const remove = async () => {
    if (!existingRegion) return;
    try {
      await onDelete(existingRegion.id);
      close();
    } catch {
      // The parent shows the API error. Keep the dialog open for retry.
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    >
      <DialogContent className="z-[950] max-w-2xl">
        <DialogHeader className="text-left">
          <DialogTitle>
            {existingRegion ? "Edit table region" : "Review table region"}
          </DialogTitle>
          <p className="text-sm text-secondary-text">
            Confirm one header row and the record rows. Review titles, totals,
            extra header rows, and touching tables before you save.
          </p>
        </DialogHeader>

        <Stack
          gap="none"
          className="max-h-[60vh] space-y-5 overflow-y-auto px-6 pb-6"
        >
          {draft && (
            <>
              <Stack gap="sm" fullWidth>
                <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-4">
                  {(
                    [
                      ["header_row", "Header row"],
                      ["record_end_row", "Last record row"],
                    ] as const
                  ).map(([key, label]) => (
                    <Stack key={key} gap="xs">
                      <Label htmlFor={`region-${key}`}>{label}</Label>
                      <Input
                        id={`region-${key}`}
                        type="number"
                        min={1}
                        aria-invalid={
                          limitFieldText[key] !== null &&
                          !/^\d+$/.test(limitFieldText[key] ?? "")
                        }
                        value={limitFieldText[key] ?? draft[key]}
                        onChange={(event) =>
                          changeRowLimit(key, event.target.value)
                        }
                      />
                    </Stack>
                  ))}
                  {/* Columns are entered as letters, because letters are what the
                      grid labels them with and what every formula on the sheet
                      refers to. Asking for "21" when the header reads "U" makes the
                      user do the conversion the spreadsheet already did. */}
                  {(
                    [
                      ["start_col", "First column"],
                      ["end_col", "Last column"],
                    ] as const
                  ).map(([key, label]) => (
                    <Stack key={key} gap="xs">
                      <Label htmlFor={`region-${key}`}>{label}</Label>
                      <Input
                        id={`region-${key}`}
                        inputMode="text"
                        autoCapitalize="characters"
                        spellCheck={false}
                        aria-invalid={
                          limitFieldText[key] !== null &&
                          !isColumnFieldValid[key]
                        }
                        value={
                          limitFieldText[key] ??
                          formatColumnLetter(draft[key] - 1)
                        }
                        onChange={(event) =>
                          changeColumnLimit(key, event.target.value)
                        }
                      />
                    </Stack>
                  ))}
                </div>

                {/* The same A1 range Excel would show for this table. */}
                <Typography variant="bodyW3" textColor="secondary">
                  {formatColumnLetter(draft.start_col - 1)}
                  {draft.header_row}:{formatColumnLetter(draft.end_col - 1)}
                  {draft.record_end_row} &middot;{" "}
                  {draft.record_end_row - draft.record_start_row + 1} records
                  &middot; {draft.end_col - draft.start_col + 1} columns
                </Typography>
              </Stack>

              <SpreadsheetRegionColumnControls
                cells={sheet.cells}
                draft={draft}
                onChange={(nextDraft) => {
                  setDraft(nextDraft);
                  onPreviewChange(nextDraft);
                }}
              />
            </>
          )}

          {validationErrors.length > 0 && (
            <Stack
              gap="none"
              className="rounded-lg bg-surface-secondary p-3 text-sm text-destructive"
            >
              {validationErrors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </Stack>
          )}
          {existingRegion && !existingRegion.validation.valid && (
            <Stack
              gap="none"
              className="rounded-lg bg-surface-secondary p-3 text-sm text-destructive"
            >
              {existingRegion.validation.errors.map((error) => (
                <p key={error}>{error}</p>
              ))}
            </Stack>
          )}
        </Stack>

        <DialogFooter>
          {existingRegion && (
            <Button
              variant="destructiveSecondary"
              size="sm"
              className="sm:mr-auto"
              disabled={isSaving}
              onClick={() => void remove()}
            >
              Remove table
            </Button>
          )}
          <Button variant="tertiary" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={isSaving}
            disabled={!draft || validationErrors.length > 0}
            onClick={() => void save()}
          >
            Confirm table
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
